import { ZipaTransferPanel } from './ZipaTransferPanel';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberZipaHistoryDto } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { safeFormatDate } from '../../../lib/format';
import { useAuthStore } from '../../../store/auth.store';
import { errorMessage } from './errorMessage';
import { LedgerAdjustForm } from './LedgerAdjustForm';
import { LedgerHistoryPager } from './LedgerHistoryPager';
import { formatPoints } from './ledgerTabs';

const PAGE_SIZE = 20;

/** ZIPA deposits, uses and permission-gated manager adjustments. */
export function ZipaTab({ memberId }: { memberId: string }) {
  const { t } = useTranslation('member');
  const qc = useQueryClient();
  const canAdjust = useAuthStore((s) => s.allowedActions?.includes('member:adjust_points'));
  const canManage = useAuthStore((s) => s.allowedActions?.includes('member:wallet_manage'));
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  const { data, isError } = useQuery<MemberZipaHistoryDto>({
    queryKey: ['member-zipa', memberId, page],
    queryFn: () => api.get(`/members/${memberId}/zipa`, { params: { page, limit: PAGE_SIZE } }),
  });

  const post = useMutation({
    mutationFn: (input: { operation: string; amount: string; note: string }) =>
      api.post(`/members/${memberId}/zipa`, {
        type: input.operation.startsWith('ADJUST_') ? 'ADJUST' : input.operation,
        ...(input.operation.startsWith('ADJUST_') ? { direction: input.operation.slice(7) } : {}),
        amount: input.amount,
        note: input.note,
      }),
    onSuccess: () => {
      setError('');
      setPage(1);
      void qc.invalidateQueries({ queryKey: ['member-zipa', memberId] });
      void qc.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
      void qc.invalidateQueries({ queryKey: ['member', memberId] });
      void qc.invalidateQueries({ queryKey: ['zipa-daily-totals'] });
    },
    onError: (failure) => setError(errorMessage(failure, t('ledgerLoadFailed'))),
  });

  return (
    <div className="space-y-4">
      <ZipaTransferPanel memberId={memberId} />
      <div className="bg-base-300 rounded-lg p-4">
        <p className="text-xs text-base-content/60 mb-1">{t('ledgerZipaBalance')}</p>
        <p className="text-2xl font-bold text-primary">
          NT${data ? formatPoints(data.balance) : '-'}
        </p>
      </div>

      {canAdjust && (
        <LedgerAdjustForm
          operations={[
            { value: 'DEPOSIT', label: t('ledgerDeposit') },
            { value: 'USE', label: t('ledgerUse') },
            ...(canManage
              ? [
                  { value: 'ADJUST_ADD', label: t('zipaAdjustAdd') },
                  { value: 'ADJUST_DEDUCT', label: t('zipaAdjustDeduct') },
                ]
              : []),
          ]}
          amountLabel={t('ledgerAmountMoney')}
          step="0.01"
          pending={post.isPending}
          error={error}
          onSubmit={(input) => post.mutate(input)}
        />
      )}

      <div className="bg-base-200 border border-base-300 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-base-300">
            <tr>
              <th className="text-left px-4 py-2">{t('ledgerType')}</th>
              <th className="text-left px-4 py-2">{t('ledgerMoneyDelta')}</th>
              <th className="text-left px-4 py-2">{t('ledgerNote')}</th>
              <th className="text-left px-4 py-2">{t('ledgerTime')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-300">
            {data?.rows.map((row) => (
              <tr key={row.id}>
                <td className="px-4 py-2">{t(`ledgerZipaType_${row.type}`)}</td>
                <td
                  className={`px-4 py-2 font-mono font-medium ${Number(row.delta) < 0 ? 'text-error' : 'text-success'}`}
                >
                  {Number(row.delta) > 0 ? '+' : ''}
                  {formatPoints(row.delta)}
                </td>
                <td className="px-4 py-2 [overflow-wrap:anywhere]">{row.note}</td>
                <td className="px-4 py-2 text-base-content/60 text-xs whitespace-nowrap">
                  {safeFormatDate(row.createdAt, (d) => d.toLocaleString())}
                </td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-base-content/50">
                  {t('ledgerEmpty')}
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-error">
                  {t('ledgerLoadFailed')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {data && (
          <LedgerHistoryPager
            page={data.page}
            limit={data.limit}
            total={data.total}
            onPage={setPage}
          />
        )}
      </div>
    </div>
  );
}
